import { type ReactNode } from 'react';

import { Callout, Intent, OverlayToaster, type Toaster, type ToastProps } from '@blueprintjs/core';
import { ErrorIcon } from '@blueprintjs/icons';

import { type ErrorMessage } from 'interfaces/enums';

import {
  ErrorResponse,
  ErrorResponseFromJSON,
  FetchError,
  RequiredError,
  ResponseError,
} from '../../generated-sources/openapi';
import i18n from '../../i18n/i18n';

import './Toast.style.sass';

let toasterPromise: Promise<Toaster> | undefined;

function getToaster(): Promise<Toaster> {
  toasterPromise ??= OverlayToaster.create().catch((error: unknown) => {
    // Allow a later call to retry creation instead of permanently caching a rejected promise.
    toasterPromise = undefined;
    throw error;
  });
  return toasterPromise;
}

async function showToast(toastMessage: ReactNode) {
  const toastProps: ToastProps = {
    message: toastMessage,
    timeout: -1,
    intent: 'danger',
  };
  (await getToaster()).show(toastProps);
}

function createDefaultMessage(errorString: string | undefined) {
  return (
    <>
      <h3> An unknown error occurred</h3>
      <br />
      <Callout intent="danger" icon={<ErrorIcon />}>
        {errorString !== undefined && errorString}
      </Callout>
    </>
  );
}

function createMessage(errorMessage: ErrorMessage | ErrorResponse): ReactNode {
  if (!errorMessage.errorType) {
    return createDefaultMessage(JSON.stringify(errorMessage));
  }
  return (
    <>
      <h3> An Error occurred while fetching data from the server</h3>
      <div>Status: {errorMessage.status}</div>
      <div>Code: {errorMessage.title}</div>
      <div>Type: {errorMessage.errorType}</div>
      <div>Message: {errorMessage.detail}</div>
      <div>TraceId: {errorMessage.traceId}</div>

      {errorMessage.additionalInformation !== undefined && <br />}
      {errorMessage.additionalInformation !== undefined && <div>Additional Information</div>}
      {errorMessage.additionalInformation?.map((info, index) => (
        <li className="item" key={`${info.reason}_${index}`}>
          {i18n.t(info.reason)}
        </li>
      ))}
      <br />
      <div>Please contact technical support and provide the above error status, code, traceId and error message.</div>
      <br />
    </>
  );
}

export async function alertToast(error: unknown): Promise<void> {
  try {
    if (error instanceof ResponseError) {
      const errorPayload: unknown = await error.response.json();
      const errorResponse = ErrorResponseFromJSON(errorPayload);

      await showToast(createMessage(errorResponse));
    } else if (error instanceof RequiredError || error instanceof FetchError) {
      await showToast(createDefaultMessage(error.message));
    } else {
      await showToast(createDefaultMessage(error instanceof Error ? error.message : String(error)));
    }
  } catch (error_) {
    // alertToast is frequently used as a fire-and-forget error handler (e.g. `.catch(alertToast)`),
    // so any failure while showing the toast (including toaster-creation failures) must not
    // reject this promise and turn into an unhandled promise rejection.
    console.error('Failed to show error toast', error_);
  }
}

export async function showSuccessToast(toastMessage: string) {
  const toastProps: ToastProps = {
    message: toastMessage,
    timeout: 5000,
    intent: Intent.SUCCESS,
  };
  (await getToaster()).show(toastProps);
}

export async function showWarningToast(toastMessage: string) {
  const toastProps: ToastProps = {
    message: toastMessage,
    timeout: -1,
    intent: Intent.WARNING,
  };
  (await getToaster()).show(toastProps);
}
